import React, { useState, useEffect, useRef } from 'react';
import './InteractiveDiagram.css';

export default function InteractiveDiagram() {
  const [activeSpot, setActiveSpot] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const stickyRef = useRef(null);
  const wrapperRef = useRef(null);
  const touchStartX = useRef(null);

  const spots = [
    {
      title: 'Dual Smart Sensors',
      spec: '6g Weight | 1000Hz IMU | 6-Axis Motion',
      desc: 'Each sensor weighs only 6 grams and locks into the silicone strap. The IMU captures motion at 1,000 samples/second, every touch, sprint, and kick tracked with elite precision.'
    },
    {
      title: 'Smart Charging Case',
      spec: 'USB-C | Rugged ABS | Magnetic Lock',
      desc: 'Up to 5 full recharges on a single case charge. Impact-resistant shell with premium magnetic closures, built for kit bags, changing rooms, and everything in between.'
    },
    {
      title: 'LED Status Indicators',
      spec: 'Color-coded | Bluetooth Sync | Battery',
      desc: 'Slow blue pulse = Bluetooth active. Solid green = fully charged. Red flash = low battery. Know your gear status at a glance, no app required.'
    },
    {
      title: 'Silicone Boot Straps',
      spec: 'All-weather | Cleat mountable | Zero interference',
      desc: 'Wraps securely around any soccer cleat without affecting your touch or comfort. Engineered silicone holds through tackles, sprints and wet conditions.'
    }
  ];

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 900);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sticky scroll driver (desktop only)
  useEffect(() => {
    if (isMobile) return; // Skip scroll animation on mobile
    const handleScroll = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const scrollable = wrapperRef.current.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const p = Math.max(0, Math.min(1, scrolled / scrollable));
      setProgress(p);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Ease in-out cubic
  const t = progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

  // Animation triggers between 10% and 85% scroll through the section
  const ease = Math.max(0, Math.min(1, (t - 0.10) / 0.75));
  const isDocked = ease > 0.93;

  // Tracker pods start at the sensor on the boot image, end in case slots
  // Boot image: left:30px, top:60px, size 320×280px
  // Sensor body center ≈ (30 + 320*0.72, 60 + 280*0.40) = (260, 172)
  // Case image: right side. In a 660px wide canvas: left≈360px, top≈220px, size 240×200px
  // Left slot center ≈ (360 + 240*0.38, 220 + 200*0.72) = (451, 364)
  // Right slot center ≈ (360 + 240*0.62, 220 + 200*0.72) = (509, 364)

  const startX = 250, startY = 128;
  const leftEndX = 445, leftEndY = 358;
  const rightEndX = 505, rightEndY = 358;

  const lx = startX + (leftEndX - startX) * ease;
  const ly = startY + (leftEndY - startY) * ease;
  const rx = startX + (rightEndX - startX) * ease;
  const ry = startY + (rightEndY - startY) * ease;

  return (
    // wrapperRef is tall (300vh) to give scroll room; stickyRef pins inside
    <div className="diagram-scroll-wrapper" ref={wrapperRef} id="specs">
      <div className="diagram-sticky" ref={stickyRef}>
        <div className="container">

          <div className="section-header text-center">
            <span className="badge-volt">DYNAMIC BLUEPRINT</span>
            <h2 className="section-title">THE TECH UNDER THE HOOD</h2>
            <p className="section-subtitle">
              Click the hotspot nodes to explore specs, scroll to watch the sensors dock.
            </p>
          </div>

          <div className="diagram-grid">

            {/* ─── Left: big animated canvas ─── */}
            <div className="diagram-visual flex-center">
              <div className="interactive-anim-canvas">

                {/* ── BOOT + STRAP (large, top-left) ── */}
                <div className="anim-boot-large">
                  <div className="boot-img-wrapper" style={{ position: 'relative', width: '100%', height: 'auto', display: 'flex' }}>
                    <img
                      src="/image_232.webp"
                      alt="Playermaker Canada, Cleat Mounted Smart Soccer Tracking Device"
                      className="boot-img-large"
                      loading="lazy"
                    />
                    {/* Positioning wrapper, animation on inner span only */}
                    <div className="sensor-led-wrap">
                      <span className={`sensor-led-pulse${isDocked ? ' led-off' : ''}`} />
                    </div>
                  </div>
                  <span className="boot-caption">Strap &amp; Sensor</span>
                </div>

                {/* ── Motion path SVG ── */}
                <svg className="anim-path-svg" style={{ overflow: 'visible' }}>
                  <defs>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="blur"/>
                      <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                  </defs>
                  {/* Left pod arc */}
                  <path
                    d={`M ${startX},${startY} C ${startX + 60},${startY + 80} ${leftEndX - 60},${leftEndY - 60} ${leftEndX},${leftEndY}`}
                    fill="none"
                    stroke="#0082FC"
                    strokeWidth="1.5"
                    strokeDasharray="6 5"
                    opacity={ease > 0.03 && !isDocked ? 0.45 : 0}
                    filter="url(#glow)"
                    style={{ transition: 'opacity 0.4s' }}
                  />
                  {/* Right pod arc */}
                  <path
                    d={`M ${startX},${startY} C ${startX + 80},${startY + 90} ${rightEndX - 40},${rightEndY - 60} ${rightEndX},${rightEndY}`}
                    fill="none"
                    stroke="#0082FC"
                    strokeWidth="1.5"
                    strokeDasharray="6 5"
                    opacity={ease > 0.03 && !isDocked ? 0.45 : 0}
                    filter="url(#glow)"
                    style={{ transition: 'opacity 0.4s' }}
                  />
                </svg>

                {/* ── CHARGING CASE (bottom-right) ── */}
                <div className={`anim-case-large${activeSpot === 1 ? ' active-highlight' : ''}`}>
                  <span className="case-caption">Charging Case</span>
                  <div className="case-img-wrap">
                    <img
                      src={isDocked ? '/playermaker_case_sensors.webp' : '/playermaker_case.webp'}
                      alt="Playermaker 2.0 Smart Charging Case and Tracker Pods"
                      className="case-img-large"
                      loading="lazy"
                    />
                    {/* Case status LED indicator when docked */}
                    {isDocked && <span className="case-status-led led-blue-idle" />}
                  </div>
                </div>

                {/* ── L tracker pod, real device SVG ── */}
                <div
                  className="tracker-pod-real"
                  style={{
                    transform: `translate(${lx}px, ${ly}px) scale(${isDocked ? 0.75 : 1 - ease * 0.12})`,
                    opacity: isDocked ? 0 : 1,
                    transition: 'opacity 0.35s ease',
                  }}
                >
                  <svg viewBox="0 0 54 86" className="pod-svg" xmlns="http://www.w3.org/2000/svg">
                    {/* Drop shadow filter */}
                    <defs>
                      <filter id="podShadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.6"/>
                      </filter>
                      <filter id="ledGlowL">
                        <feGaussianBlur stdDeviation="1.5" result="blur"/>
                        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                      </filter>
                    </defs>
                    {/* Main body */}
                    <rect x="2" y="2" width="50" height="82" rx="7" ry="7" fill="#2a2b2e" filter="url(#podShadow)"/>
                    {/* Edge highlight left */}
                    <rect x="2" y="2" width="2" height="82" rx="1" fill="#3a3b3e" opacity="0.6"/>
                    {/* Top connector area, dark inset panel */}
                    <rect x="4" y="4" width="46" height="22" rx="4" fill="#1e1f22"/>
                    {/* Connector pin slot */}
                    <rect x="18" y="8" width="18" height="4" rx="2" fill="#141517"/>
                    <rect x="21" y="9" width="4" height="2" rx="1" fill="#3a3a40"/>
                    <rect x="29" y="9" width="4" height="2" rx="1" fill="#3a3a40"/>
                    {/* Blue LED dot */}
                    <circle cx="27" cy="20" r="2.8" fill="#0082FC" className="pod-led-dot" filter="url(#ledGlowL)"/>
                    {/* Playermaker "p" logo, bottom section */}
                    {/* Circle of the p */}
                    <circle cx="26" cy="60" r="9" fill="none" stroke="#4a4b50" strokeWidth="5.5"/>
                    {/* Stem of the p */}
                    <rect x="14" y="51" width="5.5" height="20" rx="2.5" fill="#4a4b50"/>
                    {/* Bottom edge detail */}
                    <rect x="12" y="78" width="30" height="2" rx="1" fill="#1e1f22"/>
                  </svg>
                  {/* Breathing LED overlay for animation */}
                  <span className="bt-led pod-led-anim" style={{ animationDelay: '0s' }} />
                </div>

                {/* ── R tracker pod, real device SVG ── */}
                <div
                  className="tracker-pod-real"
                  style={{
                    transform: `translate(${rx}px, ${ry}px) scale(${isDocked ? 0.75 : 1 - ease * 0.12})`,
                    opacity: isDocked ? 0 : 1,
                    transition: 'opacity 0.35s ease',
                  }}
                >
                  <svg viewBox="0 0 54 86" className="pod-svg" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="podShadow2" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.6"/>
                      </filter>
                      <filter id="ledGlowR">
                        <feGaussianBlur stdDeviation="1.5" result="blur"/>
                        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                      </filter>
                    </defs>
                    <rect x="2" y="2" width="50" height="82" rx="7" ry="7" fill="#2a2b2e" filter="url(#podShadow2)"/>
                    <rect x="2" y="2" width="2" height="82" rx="1" fill="#3a3b3e" opacity="0.6"/>
                    <rect x="4" y="4" width="46" height="22" rx="4" fill="#1e1f22"/>
                    <rect x="18" y="8" width="18" height="4" rx="2" fill="#141517"/>
                    <rect x="21" y="9" width="4" height="2" rx="1" fill="#3a3a40"/>
                    <rect x="29" y="9" width="4" height="2" rx="1" fill="#3a3a40"/>
                    <circle cx="27" cy="20" r="2.8" fill="#00d2ff" className="pod-led-dot" filter="url(#ledGlowR)"/>
                    <circle cx="26" cy="60" r="9" fill="none" stroke="#4a4b50" strokeWidth="5.5"/>
                    <rect x="14" y="51" width="5.5" height="20" rx="2.5" fill="#4a4b50"/>
                    <rect x="12" y="78" width="30" height="2" rx="1" fill="#1e1f22"/>
                  </svg>
                  <span className="bt-led pod-led-anim" style={{ animationDelay: '1.5s' }} />
                </div>

                {/* ── Pulsing hotspot nodes ── */}
                <button id="hs-sensors" className={`hotspot spot-sensors${activeSpot === 0 ? ' active' : ''}`} onClick={() => setActiveSpot(0)} aria-label="Smart Sensors"><span className="hotspot-pulse"/><span className="hotspot-number">1</span></button>
                <button id="hs-case"    className={`hotspot spot-case${activeSpot === 1 ? ' active' : ''}`}    onClick={() => setActiveSpot(1)} aria-label="Charging Case"><span className="hotspot-pulse"/><span className="hotspot-number">2</span></button>
                <button id="hs-leds"   className={`hotspot spot-leds${activeSpot === 2 ? ' active' : ''}`}   onClick={() => setActiveSpot(2)} aria-label="LED Indicators"><span className="hotspot-pulse"/><span className="hotspot-number">3</span></button>
                <button id="hs-straps" className={`hotspot spot-straps${activeSpot === 3 ? ' active' : ''}`} onClick={() => setActiveSpot(3)} aria-label="Boot Straps"><span className="hotspot-pulse"/><span className="hotspot-number">4</span></button>

              </div>
            </div>

            {/* ─── Right: spec detail panel ─── */}
            <div
              className="diagram-details card-tech flex-center"
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchStartX.current === null) return;
                const delta = touchStartX.current - e.changedTouches[0].clientX;
                if (Math.abs(delta) > 40) {
                  setActiveSpot((prev) =>
                    delta > 0
                      ? Math.min(prev + 1, spots.length - 1)  // swipe left → next
                      : Math.max(prev - 1, 0)                  // swipe right → prev
                  );
                }
                touchStartX.current = null;
              }}
            >
              <div className="details-content animate-fade-in" key={activeSpot}>
                <span className="details-number">0{activeSpot + 1}</span>
                <h3 className="details-title">{spots[activeSpot].title}</h3>
                <p className="details-spec">{spots[activeSpot].spec}</p>
                <p className="details-desc">{spots[activeSpot].desc}</p>
                <div className="details-nav-dots">
                  {spots.map((_, i) => (
                    <button
                      key={i}
                      className={`nav-dot${activeSpot === i ? ' active' : ''}`}
                      onClick={() => setActiveSpot(i)}
                      aria-label={`Spec ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
